import React from 'react';
import { useGetMeteocodesByCodesStringsQuery } from 'Services/api/meteoCodesApi';
import {
  usePullDatasourceTS2Query,
} from 'Services/api/reportsApi';
import ChartWrapper from 'Shared/Chart/ChartWrapper';
import Chart from 'Shared/Chart/Chart';
import { useChartjsCustomLegend } from 'lib/DemoWidgets/CustomLegend/useChartjsCustomLegend';
import { useChartjsProjectionChart } from 'lib/DemoWidgets/ProjectionChart/useChartjsProjectionChart';
import { Line } from 'react-chartjs-2';
import { Pane } from 'evergreen-ui';
import {
  CHART_TYPES,
  EMPTY_ARRAY,
  changeScaleDisplayByDatasetsProperties,
  extractMeteocodesIds,
  getChartData,
  getChartScales,
  getQuery,
  getTimeUtcDataConverterByZone,
  msInHour,
} from 'Features/StationsNew/Datasources/Datasource/Monitors/utils';
import { ChartWidgetOptions } from 'Features/StationsNew/Datasources/Datasource/Monitors/types';
import { LegendTabWrapper, LegendTabsWrapper, LegendTab } from './TabsLegend';

type LinearPlusProps = {
  options: ChartWidgetOptions
  zone: string
  pollingInterval: number
  title?: string
};

export function LinearPlus({
  title = '', options, zone, pollingInterval,
}: LinearPlusProps) {
  // ============= COPY PASTE START =============
  const { datasetsInfo } = options;
  const meteocodesIds = React.useMemo(() => extractMeteocodesIds(datasetsInfo), [datasetsInfo]);
  const meteocodesQuery = useGetMeteocodesByCodesStringsQuery(meteocodesIds);
  const query = React.useMemo(() => getQuery(options), [options]);
  const result = usePullDatasourceTS2Query(query, {
    pollingInterval,
    skip: !datasetsInfo.length,
  });

  const meteocodes = meteocodesQuery.data ?? EMPTY_ARRAY;
  const chartsData = result.data ?? EMPTY_ARRAY;

  const { firstYKey, chartjsScales }: any = React.useMemo(() => (
    getChartScales(datasetsInfo, meteocodes) || {}
  ), [datasetsInfo, meteocodes]);

  const convertData = React.useMemo(() => getTimeUtcDataConverterByZone(zone), [zone]);

  const chartData = React.useMemo(() => (
    getChartData(datasetsInfo, chartsData, meteocodes, firstYKey, convertData)
  ), [chartsData, datasetsInfo, firstYKey, meteocodes, convertData]);

  const isLoading = meteocodesQuery.isLoading || result.isLoading;

  // ============= COPY PASTE END =============
  const { provideForProjectionChartPlugin, projectionChartConfig } = useChartjsProjectionChart();
  const { datasetsProperties, customLegendPlugin } = useChartjsCustomLegend();

  const scalesWithDisplay = React.useMemo(
    () => changeScaleDisplayByDatasetsProperties(chartjsScales, datasetsProperties),
    [chartjsScales, datasetsProperties],
  );

  const subChartSectionHeight = 50;
  const legendHeight = 70;
  projectionChartConfig.options.maintainAspectRatio = false;
  projectionChartConfig.options.responsive = true;
  projectionChartConfig.options.plugins.tooltip = {
    enabled: false,
  };

  const tabs = React.useMemo(() => {
    if (datasetsProperties.length !== datasetsInfo.length || meteocodes.length !== datasetsInfo.length) {
      return [];
    }

    return datasetsProperties.map((datasetProperties) => {
      const datasetInfo = datasetsInfo[datasetProperties.datasetIndex];
      const meteocode = meteocodes[datasetProperties.datasetIndex];

      return (
        <LegendTabWrapper
          key={datasetInfo.dataInfo.meteoCode}
          color={datasetProperties.dataset.backgroundColor as string}
          onClick={datasetProperties.toggleVisibility}
          isHidden={datasetProperties.isHidden}
        >
          <LegendTab
            meteocode={meteocode}
          />
        </LegendTabWrapper>
      );
    });
  }, [datasetsInfo, datasetsProperties, meteocodes]);

  return (
    <ChartWrapper>
      {(width: number, height: number) => (
        <>
          <LegendTabsWrapper
            legendHeight={legendHeight}
          >
            {tabs}
          </LegendTabsWrapper>
          <Chart
            type={CHART_TYPES.LINE}
            disableZoom
            chartTitle={title}
            chartOptions={{ width, height: height - subChartSectionHeight - legendHeight, confines: [] }}
            datasets={chartData.datasets}
            scales={scalesWithDisplay}
            loadOnClick={isLoading}
            loadOnClickOptions={{
              isLoading,
              onClick: null,
              isSuccess: false,
            }}
            plugins={[provideForProjectionChartPlugin, customLegendPlugin]}
            timeFormatPostfix={`UTC${zone}`}
            minZoomRange={msInHour}
          />
          <Pane
            width={width}
            height={subChartSectionHeight}
          >
            <Line
              {...projectionChartConfig}
            />
          </Pane>
        </>
      )}
    </ChartWrapper>
  );
}
